import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown } from '../../../svgs/svgs';
import useEnumsStore from '../../../store/Enums';
import StatusPointer from '../StatusPointer/StatusPointer';

type PopoverType = 'status' | 'priority' | 'department' | 'project-status';

interface PopoverOption {
  value: string;
  label: string;
  color: string;
  bg?: string;
}

interface PopoverWithTypeProps {
  defaultValue?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  type?: PopoverType;
  onChange?: (value: string) => void;
  withPointer?: boolean;
}

const PRIORITY_STYLE_MAP: Record<string, { color: string; bg: string }> = {
  p0: {
    color: 'border-[#4DAD52] bg-[#4DAD52]/10 text-[#4DAD52] border-0.5',
    bg: 'bg-[#4DAD52]',
  },
  p1: {
    color: 'border-[#9BA735] bg-[#9BA735]/10 text-[#9BA735] border-0.5',
    bg: 'bg-[#9BA735]',
  },
  p2: {
    color: 'border-[#954281] bg-[#954281]/10 text-[#954281] border-0.5',
    bg: 'bg-[#954281]',
  },
  p3: {
    color: 'border-[#225668] bg-[#225668]/10 text-[#225668]',
    bg: 'bg-[#225668]',
  },
  '': {
    color:
      'border-navigation-accent-4 bg-[#6F6F6F24] text-input-inactive-placeholder',
    bg: 'bg-[#6F6F6F44]',
  },
};

const STATUS_STYLE_MAP: Record<string, string> = {
  active:
    'border-[#AD98FE] bg-[#AD98FE]/20 text-[#AD98FE] border-0.5 border-0.5',
  todo: 'border-navigation-accent-4 bg-[#6F6F6F24] text-input-inactive-placeholder',
  inreview:
    'border-navigation-accent-2 bg-[#DB8734]/10 text-navigation-accent-2 border-0.5 border-0.5',
  paused:
    'border-[#962B81] bg-[#962B81]/10 text-[#962B81] border-0.5 border-0.5',
  blocked:
    'border-[#D65757] bg-[#D65757]/10 text-[#D65757] border-0.5 border-0.5',
  completed:
    'border-navigation-accent-3 bg-[#40B18F]/10 text-navigation-accent-3 border-0.5 border-0.5',
};

const DEPARTMENT_STYLE_MAP: Record<string, string> = {
  // '': 'border-navigation-accent-4 bg-[#6F6F6F24] text-input-inactive-placeholder',
  design: 'border-[#5E7F21] bg-[#5E7F21]/10 text-[#95AB6E] border-0.5',
  data: 'border-[#DD9108] bg-[#8F5D04]/10 text-[#DD9108] border-0.5',
  product: 'border-[#E22E89] bg-[#E22E89]/10 text-[#E22E89] border-0.5',
  frontendengineering: 'border-[#0CAAAF] bg-[#164C3B] text-white-1 border-0.5',
  backendengineering: 'border-[#2E59BB] bg-[#213D7C] text-white-1 border-0.5',
};

const DEFAULT_COLOR_CLASS = 'border-modal-stroke bg-modal-input';
const DEFAULT_BG_CLASS = 'bg-[#6F6F6F44]';

const normalizeValue = (value: string) =>
  value.toLowerCase().replace(/[\s_-]/g, '');

export default function PopoverWithType({
  defaultValue = '',
  placement = 'bottom',
  size = 'md',
  type = 'status',
  onChange,
  withPointer,
}: PopoverWithTypeProps) {
  const { taskStatuses, taskPriorities, taskDepartments, projectStatuses } =
    useEnumsStore();
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    finalPlacement: placement,
  });
  const triggerRef = useRef<HTMLDivElement>(null);

  // Sync internal state with prop changes
  useEffect(() => {
    setSelectedValue(defaultValue);
  }, [defaultValue]);

  const getOptionStyle = (optionType: PopoverType, value: string) => {
    if (optionType === 'priority') {
      return PRIORITY_STYLE_MAP[value.toLowerCase()] || PRIORITY_STYLE_MAP[''];
    }

    if (optionType === 'department') {
      return {
        color:
          DEPARTMENT_STYLE_MAP[normalizeValue(value)] || DEFAULT_COLOR_CLASS,
        bg: DEFAULT_BG_CLASS,
      };
    }

    if (optionType === 'status' || optionType === 'project-status') {
      return {
        color: STATUS_STYLE_MAP[normalizeValue(value)] || DEFAULT_COLOR_CLASS,
        bg: DEFAULT_BG_CLASS,
      };
    }

    return { color: DEFAULT_COLOR_CLASS, bg: DEFAULT_BG_CLASS };
  };

  const getOptions = (): PopoverOption[] => {
    const fallbackTaskStatuses = [
      'Active',
      'To-Do',
      'In Review',
      'Paused',
      'Blocked',
      'Completed',
    ];
    const fallbackTaskPriorities = ['P0', 'P1', 'P2', 'P3'];
    const fallbackTaskDepartments = [
      'Design',
      'Data',
      'Product',
      'Frontend Engineering',
      'Backend Engineering',
    ];
    const fallbackProjectStatuses = [
      'To-Do',
      'Active',
      'Paused',
      'Blocked',
      'Completed',
    ];

    const values =
      type === 'status'
        ? taskStatuses.length > 0
          ? taskStatuses
          : fallbackTaskStatuses
        : type === 'priority'
          ? taskPriorities.length > 0
            ? taskPriorities
            : fallbackTaskPriorities
          : type === 'department'
            ? taskDepartments.length > 0
              ? taskDepartments
              : fallbackTaskDepartments
            : projectStatuses.length > 0
              ? projectStatuses
              : fallbackProjectStatuses;

    const mapped = values.map((value) => ({
      value,
      label: value,
      ...getOptionStyle(type, value),
    }));

    if (type === 'priority') {
      return [
        ...mapped,
        {
          value: '',
          label: 'P-',
          ...getOptionStyle(type, ''),
        },
      ];
    }

    if (type === 'department') {
      return [...mapped];
    }

    return mapped;
  };

  // Color logic for the selected value (trigger button only)
  const getColorClasses = (val: string) => {
    const options = getOptions();
    const option =
      options.find((opt) => opt.value === val) ||
      options.find((opt) => normalizeValue(opt.value) === normalizeValue(val));

    if (option) {
      return {
        colorClass: option.color,
        bg: option.bg,
        text: 'text-white',
      };
    }

    // Fallback
    return {
      colorClass: 'border-modal-stroke bg-modal-input',
      bg: '',
      text: 'text-text-primary-body',
    };
  };

  const colors = getColorClasses(selectedValue);

  // Size mapping for popover content
  const sizeClasses = {
    xs: 'min-w-[120px] max-w-[250px]',
    sm: 'min-w-[150px] max-w-[250px]',
    md: 'min-w-[200px] max-w-[350px]',
    lg: 'min-w-[300px] max-w-[500px]',
    xl: 'min-w-[400px] max-w-[650px]',
  };

  // Size mapping for trigger button
  const triggerSizeClasses = {
    xs: 'h-[32px] px-2 py-1 text-sm',
    sm: 'h-[36px] px-2 py-1',
    md: 'h-[44px] px-2 py-1',
    lg: 'h-[52px] px-3 py-2 text-lg',
    xl: 'h-[60px] px-4 py-2 text-xl',
  };

  const calculatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const baseTop = rect.top + window.scrollY;
    const baseLeft = rect.left + window.scrollX;

    let finalPlacement = placement;
    let top, left;

    switch (finalPlacement) {
      case 'top':
        top = baseTop - gap;
        left = baseLeft;
        break;
      case 'bottom':
        top = baseTop + rect.height + gap;
        left = baseLeft;
        break;
      case 'left':
        top = baseTop + rect.height / 2;
        left = baseLeft - gap;
        break;
      case 'right':
        top = baseTop + rect.height / 2;
        left = baseLeft + rect.width + gap;
        break;
      default:
        top = baseTop + rect.height + gap;
        left = baseLeft;
    }

    setPosition({ top, left, finalPlacement });
  };

  const handleToggle = () => {
    if (!open) calculatePosition();
    setOpen((prev) => !prev);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      triggerRef.current &&
      !triggerRef.current.contains(e.target as Node) &&
      !document.getElementById('popover-content')?.contains(e.target as Node)
    ) {
      setOpen(false);
    }
  };

  const handleOptionClick = (optionValue: any) => {
    setSelectedValue(optionValue);
    if (onChange) {
      onChange(optionValue);
    }
    setOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, []);

  const options = getOptions();
  const currentOption =
    options.find((opt) => opt.value === selectedValue) ||
    options.find(
      (opt) => normalizeValue(opt.value) === normalizeValue(selectedValue)
    );

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        className={`inline-flex items-center justify-between gap-2 cursor-pointer select-none w-full max-w-[200px]
           ${colors.colorClass} ${colors.text} ${triggerSizeClasses[size]}
           rounded-md font-medium`}
      >
        <div className="w-full flex items-center gap-2">
          {(type === 'status' || type === 'project-status') && withPointer && (
            <StatusPointer size={12} status={currentOption?.value} />
          )}
          <p className="w-full max-w-[90px] truncate">
            {currentOption?.label || selectedValue}
          </p>
        </div>
        <div
          className={`transition-transform duration-300 ${
            open ? 'rotate-180' : 'rotate-0'
          }`}
        >
          <ArrowDown size={16} />
        </div>
      </div>

      {open &&
        createPortal(
          <div
            id="popover-content"
            className={`absolute z-[9999] bg-frame-inner border border-frame-stroke 
              text-text-primary-body rounded-xl shadow-lg p-2
              animate-fade-in ${sizeClasses[size] || sizeClasses.md}`}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform:
                position.finalPlacement === 'left'
                  ? 'translate(-100%, -50%)'
                  : position.finalPlacement === 'right'
                    ? 'translate(0, -50%)'
                    : position.finalPlacement === 'top'
                      ? 'translate(-100%)'
                      : 'translate(0, 0)',
            }}
          >
            <div className="flex flex-col gap-1">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:pl-5 hover:bg-black-7 rounded-md transition-all duration-300 ease-in-out"
                >
                  {type === 'priority' && (
                    <div className={`w-5 h-5 rounded-md ${option.bg}`} />
                  )}

                  {(type === 'status' || type === 'project-status') &&
                    withPointer && (
                      <StatusPointer size={14} status={option.value} />
                    )}
                  <div
                    className={` hover:bg-white/5 
                    transition-colors duration-200 text-white text-sm font-medium
                    ${selectedValue === option.value ? 'bg-white/10' : ''} ${option.value === '' ? 'opacity-20 font-normal ' : 'block'}`}
                  >
                    {option.label}
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
